import React, { memo, useMemo } from 'react';
import { useField } from 'react-final-form';
import { addDays, differenceInDays } from 'date-fns';

import { useScopedTranslation } from '@common/utils/hooks/useScopedTranslation';
import i18n from '@i18n/index';
import {
  OfficesQuery_offices as Office,
  Timetable_timetable_groups as Group,
  Timetable_timetable_groups_rows_columns as Column,
} from '@typings/api';
import { DateStyle, formatToLocaleDateString } from '@utils/date';

import { MainFormFields } from '../BookingFlow';

import GroupContainer from './Group';
import Skeleton from './Skeleton';

interface Props {
  groups: Group[];
  isSearching: boolean;
  office: Office;
  start: Date;
  end: Date;
  timezone: string;
}

function TimeTable(props: Props) {
  const { end, groups, isSearching, office, start, timezone } = props;
  const { t } = useScopedTranslation();
  const {
    input: { onChange: setSelectedOfficeField },
  } = useField(MainFormFields.SELECTED_OFFICE);
  const {
    input: { onChange: setTimeSlotField },
  } = useField(MainFormFields.TIME_SLOT);

  const memoizedDates = useMemo(() => {
    const daysShown = differenceInDays(end, start) + 1;

    return new Array(daysShown).fill(null).map((_, i) => {
      const calculatedDate = addDays(start, i);

      return {
        name: formatToLocaleDateString(calculatedDate, i18n.language, {
          timezone,
          weekday: DateStyle.LONG,
        }),
        date: formatToLocaleDateString(calculatedDate, i18n.language, {
          day: DateStyle.NUMERIC,
          month: DateStyle.LONG,
          timezone,
        }),
      };
    });
  }, [start, end, timezone]);

  function handleSlotSelect(slot: Column) {
    setSelectedOfficeField(office);
    setTimeSlotField(slot);
  }

  return (
    <>
      <div className="space-around flex border border-gray-300 select-none">
        {memoizedDates.map(({ date, name }) => (
          <div className="flex-1 px-1 py-2 text-center" key={date}>
            <p className="font-medium">{name}</p>
            <p>{date}</p>
          </div>
        ))}
      </div>
      {isSearching && <Skeleton />}
      {!isSearching && groups?.length === 0 && (
        <div className="flex items-center justify-center mb-4 mt-2 w-full h-8">
          {t('main_form.timetable.not_found')}
        </div>
      )}
      {!isSearching && (
        <>
          {groups.map((group) => (
            <GroupContainer
              group={group}
              key={group.startTime}
              onSlotSelect={handleSlotSelect}
              timezone={timezone}
            />
          ))}
        </>
      )}
    </>
  );
}

export default memo(TimeTable);
